"use client";

import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button, Card, Upload, Modal, message } from "antd";
import {
  CameraOutlined,
  UploadOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

export default function ImageCapture({ onImageCapture }) {
  const [images, setImages] = useState([]);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const maxImages = 2;
  const canAddMore = images.length < maxImages;

  // Call onImageCapture whenever images change
  useEffect(() => {
    if (onImageCapture) {
      onImageCapture(images);
    }
  }, [images, onImageCapture]);

  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setStream(mediaStream);
      setIsCameraOpen(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error("Kamera-Zugriff fehlgeschlagen:", error);
      message.error(
        "Kamera konnte nicht geöffnet werden. Bitte Berechtigungen prüfen."
      );
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsCameraOpen(false);
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current && images.length < maxImages) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      if (context) {
        context.drawImage(video, 0, 0);
        const imageDataUrl = canvas.toDataURL("image/jpeg", 0.8);
        setImages((prev) => [...prev, imageDataUrl]);

        if (images.length + 1 >= maxImages) {
          stopCamera();
        }
      }
    }
  }, [images.length, maxImages, stopCamera]);

  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (!files) return;

    const remaining = maxImages - images.length;
    const filesToProcess = Array.from(files).slice(0, remaining);

    filesToProcess.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImages((prev) => [...prev, e.target.result]);
        };
        reader.readAsDataURL(file);
      }
    });

    // Reset input
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: 24 }}>
      <h2 style={{ textAlign: "center", fontSize: 24, fontWeight: "bold" }}>
        Bilder hinzufügen
      </h2>
      <p style={{ textAlign: "center", color: "#999" }}>
        Maximal {maxImages} Bilder – {images.length} von {maxImages} verwendet
      </p>

      {canAddMore && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            marginTop: 16,
          }}
        >
          <Button
            icon={<UploadOutlined />}
            onClick={() => fileInputRef.current.click()}
          >
            Bilder hochladen
          </Button>
          <Button
            icon={<CameraOutlined />}
            onClick={isCameraOpen ? stopCamera : startCamera}
          >
            {isCameraOpen ? "Kamera schließen" : "Kamera öffnen"}
          </Button>
        </div>
      )}

      <input
        type="file"
        accept="image/*"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileUpload}
      />

      {isCameraOpen && (
        <Card style={{ marginTop: 24 }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", borderRadius: 8 }}
          />
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Button
              type="primary"
              shape="circle"
              icon={<CameraOutlined />}
              size="large"
              onClick={capturePhoto}
              disabled={!canAddMore}
            />
          </div>
        </Card>
      )}

      <canvas ref={canvasRef} style={{ display: "none" }} />

      {images.length > 0 && (
        <div
          style={{
            marginTop: 24,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
          }}
        >
          {images.map((img, index) => (
            <Card
              key={index}
              hoverable
              cover={
                <img
                  alt={`Bild ${index + 1}`}
                  src={img}
                  style={{ height: 200, objectFit: "cover" }}
                />
              }
              actions={[
                <DeleteOutlined
                  key="delete"
                  onClick={() => removeImage(index)}
                />,
              ]}
            />
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Button icon={<ReloadOutlined />} onClick={() => setImages([])}>
            Alle Bilder entfernen
          </Button>
        </div>
      )}

      {!canAddMore && (
        <div
          style={{
            textAlign: "center",
            marginTop: 24,
            background: "#f5f5f5",
            padding: 12,
            borderRadius: 8,
          }}
        >
          <p style={{ color: "#888" }}>
            Maximum von {maxImages} Bildern erreicht
          </p>
        </div>
      )}
    </div>
  );
}
